/**
 * SELECTOR DE MOLIENDA PARA PRODUCTOS DE SUSCRIPCIÓN
 * Implementación para 69 Crazy Beans - Tema Local + Appstle Subscriptions
 * VERSIÓN 7.0
 *  - El bloque se inserta SIEMPRE debajo de la lista de productos (columna principal),
 *    nunca dentro del aside del checkout. En móvil la columna principal se apila
 *    encima del aside, así que queda entre el carrito y el checkout: mismo resultado.
 *  - Layout en rejilla para que no quede una lista larguísima a ancho completo.
 */

(function() {
  'use strict';

  // ========================================
  // CONFIGURACIÓN
  // ========================================

  const CONFIG = {
    subscriptionHandles: [
      'cafe-descafeinado-especialidad-colombia-500g-1',
      'cafe-ecologico-especialidad-colombia-500g-1',
      'cafe-especialidad-colombia-500g-1',
      'cafe-especialidad-india-500g-1',
      'cafe-especialidad-guatemala-500g-1',
      'cafe-descafeinado-especialidad-colombia-1-kg',
      'cafe-ecologico-especialidad-colombia-1k-suscripcion',
      'cafe-especialidad-colombia-1kg-suscripcion',
      'cafe-especialidad-india-1kg-suscripcion',
      'cafe-especialidad-guatemala-1kg-suscripcion'
    ],

    grindingOptions: [
      { value: '', label: 'En grano (sin moler)' },
      { value: 'espresso', label: 'Molido para Espresso' },
      { value: 'moka', label: 'Molido para Moka' },
      { value: 'chemex', label: 'Molido para Chemex' },
      { value: 'prensa-francesa', label: 'Molido para Prensa Francesa' }
    ],

    selectors: {
      checkoutButton: '#CheckOut',
      cartForm: 'form[action="/cart"]',

      // Contenedores de la lista de productos, en orden de preferencia.
      // Si tu tema usa otra clase, añádela AL PRINCIPIO de este array.
      cartItems: [
        '.cart__items',
        '.cart-items',
        '[data-cart-items]',
        'form[action="/cart"] table',
        'table.cart',
        '.cart-section table'
      ],

      // Columna principal (izquierda) como fallback.
      mainColumn: [
        '.cart-section .cart__main',
        '.cart-section__main',
        '.cart__contents',
        '.cart-section > div:not(aside)'
      ]
    },

    texts: {
      es: {
        mainTitle: '☕ ¿Quieres tu café molido?',
        mainDescription: 'Por defecto, tu café se entregará en grano. Si prefieres recibirlo molido, selecciona el tipo de molienda que necesitas para cada producto.',
        grindingLabel: 'Tipo de molienda',
        optional: '(Opcional)',
        unit: 'Unidad',
        defaultGrain: 'En grano',
        infoNote: '<strong>ℹ️ Importante:</strong> Si no seleccionas ningún tipo de molienda, tu café se entregará <strong>en grano</strong> para que lo muelas cuando lo desees y mantenga todo su aroma y frescura.'
      },
      en: {
        mainTitle: '☕ Would you like your coffee ground?',
        mainDescription: 'By default, your coffee will be delivered as whole beans. If you prefer it ground, select the grinding type you need for each product.',
        grindingLabel: 'Grinding type',
        optional: '(Optional)',
        unit: 'Unit',
        defaultGrain: 'Whole beans',
        infoNote: '<strong>ℹ️ Important:</strong> If you don\'t select a grinding type, your coffee will be delivered as <strong>whole beans</strong> so you can grind it whenever you like and keep all its aroma and freshness.'
      }
    }
  };

  // ========================================
  // UTILIDADES
  // ========================================

  const Utils = {
    getLanguage() {
      return document.documentElement.lang === 'es' ||
             window.location.pathname.includes('/es/') ? 'es' : 'en';
    },

    getText(key) {
      const lang = this.getLanguage();
      return CONFIG.texts[lang][key] || CONFIG.texts.es[key];
    },

    generateSelectorId(handle, index) {
      return `grinding-selector-${handle}-${index}`;
    },

    // Devuelve el primer elemento que exista de una lista de selectores
    queryFirst(selectorList) {
      for (const sel of selectorList) {
        const el = document.querySelector(sel);
        if (el) return el;
      }
      return null;
    },

    saveToLocalStorage(data) {
      try {
        localStorage.setItem('cart_grinding_selections', JSON.stringify(data));
      } catch (e) {
        console.warn('No se pudo guardar en localStorage:', e);
      }
    },

    loadFromLocalStorage() {
      try {
        const data = localStorage.getItem('cart_grinding_selections');
        return data ? JSON.parse(data) : {};
      } catch (e) {
        return {};
      }
    },

    clearLocalStorage() {
      try {
        localStorage.removeItem('cart_grinding_selections');
      } catch (e) {
        console.warn('No se pudo limpiar localStorage:', e);
      }
    },

    generateCartHash(cartItems) {
      return cartItems.map(item =>
        `${item.variant_id}-${item.quantity}-${item.key}`
      ).join('|');
    }
  };

  // ========================================
  // GESTOR DE CARRITO
  // ========================================

  const CartManager = {
    currentCartHash: null,

    async getCartItems() {
      try {
        const response = await fetch('/cart.js');
        const cart = await response.json();
        return cart.items;
      } catch (error) {
        console.error('Error obteniendo carrito:', error);
        return [];
      }
    },

    needsGrindingSelection(handle) {
      return CONFIG.subscriptionHandles.includes(handle);
    },

    async updateCartAttributes(attributes) {
      try {
        const response = await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ attributes })
        });
        return await response.json();
      } catch (error) {
        console.error('Error actualizando atributos del carrito:', error);
        return null;
      }
    },

    async updateLineItemProperties(lineItemKey, properties) {
      try {
        const response = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: lineItemKey, properties: properties })
        });
        return await response.json();
      } catch (error) {
        console.error('Error actualizando propiedades del item:', error);
        return null;
      }
    }
  };

  // ========================================
  // ESTILOS (una sola vez)
  // ========================================

  const Styles = {
    injected: false,

    inject() {
      if (this.injected) return;
      this.injected = true;

      const style = document.createElement('style');
      style.id = 'grinding-selector-styles';
      style.textContent = `
        #grinding-selectors-main-container {
          margin: 24px 0 0 0;
          padding: 20px;
          width: 100%;
          max-width: 100%;
          box-sizing: border-box;
          background-color: rgba(12, 35, 55, 0.02);
          border: 2px solid #0c2337;
          border-radius: 8px;
        }
        #grinding-selectors-main-container .gs-heading {
          margin: 0 0 8px 0;
          font-size: 18px;
          color: #0c2337;
          font-weight: 600;
        }
        #grinding-selectors-main-container .gs-description {
          margin: 0 0 20px 0;
          font-size: 14px;
          color: #6c757d;
          line-height: 1.5;
        }
        #grinding-selectors-main-container .gs-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
          gap: 16px;
        }
        #grinding-selectors-main-container .gs-item {
          min-width: 0;
        }
        #grinding-selectors-main-container .gs-product-title {
          font-weight: 500;
          margin-bottom: 6px;
          color: #0c2337;
          font-size: 15px;
        }
        .grinding-selector-container {
          padding: 15px;
          background-color: rgba(12, 35, 55, 0.04);
          border: 1px solid rgba(12, 35, 55, 0.15);
          border-radius: 5px;
        }
        .grinding-selector-label {
          display: block;
          margin-bottom: 8px;
          font-size: 14px;
          color: #0c2337;
          font-weight: 500;
        }
        .grinding-selector-label .gs-optional {
          color: #6c757d;
          margin-left: 5px;
          font-size: 12px;
          font-weight: 400;
        }
        select.grinding-selector {
          width: 100%;
          padding: 10px 12px;
          border: 2px solid rgba(12, 35, 55, 0.3);
          border-radius: 5px;
          font-size: 16px;
          font-family: var(--font-stack-body, inherit);
          background-color: #fff;
          color: #0c2337;
          transition: border-color 0.2s ease, background-color 0.2s ease, box-shadow 0.2s ease;
        }
        select.grinding-selector:focus-visible {
          outline: none;
          border-color: #0c2337;
          box-shadow: 0 0 0 3px rgba(12, 35, 55, 0.15);
        }
        select.grinding-selector.is-selected {
          border-color: #28a745;
          background-color: #f8fff9;
        }
        #grinding-selectors-main-container .gs-note {
          margin-top: 16px;
          padding: 12px;
          background-color: #e7f3ff;
          border-left: 4px solid #0c2337;
          border-radius: 4px;
          font-size: 13px;
          color: #0c2337;
          line-height: 1.5;
        }
        @media (prefers-reduced-motion: reduce) {
          select.grinding-selector { transition: none; }
        }
      `;
      document.head.appendChild(style);
    }
  };

  // ========================================
  // GESTOR DE INTERFAZ
  // ========================================

  const UIManager = {
    isUpdating: false,

    createGrindingSelector(itemData) {
      const { handle, index, selectorId, variantId, lineItemKey } = itemData;

      const container = document.createElement('div');
      container.className = 'grinding-selector-container';
      container.setAttribute('data-grinding-selector', selectorId);
      container.setAttribute('data-variant-id', variantId);
      container.setAttribute('data-line-key', lineItemKey);

      const label = document.createElement('label');
      label.htmlFor = selectorId;
      label.className = 'grinding-selector-label';
      label.innerHTML = `<strong>${Utils.getText('grindingLabel')}</strong>` +
        `<span class="gs-optional">${Utils.getText('optional')}</span>`;

      const select = document.createElement('select');
      select.id = selectorId;
      select.name = selectorId;
      select.className = 'grinding-selector form__field';
      select.setAttribute('data-handle', handle);
      select.setAttribute('data-index', index);
      select.setAttribute('data-variant-id', variantId);
      select.setAttribute('data-line-key', lineItemKey);

      CONFIG.grindingOptions.forEach(option => {
        const optionEl = document.createElement('option');
        optionEl.value = option.value;
        optionEl.textContent = option.label;
        if (option.value === '') optionEl.selected = true;
        select.appendChild(optionEl);
      });

      select.addEventListener('change', () => this.handleGrindingChange(select));

      container.appendChild(label);
      container.appendChild(select);

      return container;
    },

    setSelectState(select) {
      select.classList.toggle('is-selected', !!select.value);
    },

    async handleGrindingChange(selectElement) {
      const selectorId = selectElement.id;
      const value = selectElement.value;

      const selections = Utils.loadFromLocalStorage();

      if (value) {
        selections[selectorId] = {
          value: value,
          variantId: selectElement.getAttribute('data-variant-id'),
          lineItemKey: selectElement.getAttribute('data-line-key'),
          handle: selectElement.getAttribute('data-handle'),
          label: selectElement.options[selectElement.selectedIndex].text
        };
      } else {
        delete selections[selectorId];
      }

      Utils.saveToLocalStorage(selections);
      this.setSelectState(selectElement);

      await this.updateCartWithSelections();
    },

    async updateCartWithSelections() {
      const selections = Utils.loadFromLocalStorage();
      const attributes = {};
      const lineItemUpdates = {};

      const cartItems = await CartManager.getCartItems();
      const lineItemKeys = cartItems.map(item => item.key);

      Object.keys(selections).forEach(selectorId => {
        if (!lineItemKeys.includes(selections[selectorId].lineItemKey)) {
          delete selections[selectorId];
        }
      });

      Utils.saveToLocalStorage(selections);

      Object.keys(selections).forEach(selectorId => {
        const selection = selections[selectorId];
        if (selection.value) {
          attributes[selectorId] = selection.value;
          if (!lineItemUpdates[selection.lineItemKey]) {
            lineItemUpdates[selection.lineItemKey] = {};
          }
          lineItemUpdates[selection.lineItemKey]['Tipo de molienda'] = selection.label;
        }
      });

      await CartManager.updateCartAttributes(attributes);

      for (const lineItemKey in lineItemUpdates) {
        await CartManager.updateLineItemProperties(lineItemKey, lineItemUpdates[lineItemKey]);
      }

      console.log('💾 Metadatos guardados:', {
        attributes: Object.keys(attributes).length,
        lineItems: Object.keys(lineItemUpdates).length
      });
    },

    restoreSelections() {
      const selections = Utils.loadFromLocalStorage();

      Object.keys(selections).forEach(selectorId => {
        const select = document.getElementById(selectorId);
        if (select && selections[selectorId] && selections[selectorId].value) {
          select.value = selections[selectorId].value;
          this.setSelectState(select);
        }
      });
    },

    /**
     * CLAVE DEL FIX:
     * Devuelve el punto de inserción, siempre en la columna de productos.
     * Nunca dentro del aside/columna del botón de checkout.
     */
    getInsertionTarget() {
      const aside = document.querySelector('.cart-section aside');

      // 1. Justo después de la lista/tabla de productos.
      const itemsEl = Utils.queryFirst(CONFIG.selectors.cartItems);
      if (itemsEl && !(aside && aside.contains(itemsEl))) {
        const anchor = itemsEl.closest('.cart__items, .cart-items, [data-cart-items]') || itemsEl;
        return { el: anchor, mode: 'after' };
      }

      // 2. Al final de la columna principal.
      const mainCol = Utils.queryFirst(CONFIG.selectors.mainColumn);
      if (mainCol && mainCol !== aside && !(aside && aside.contains(mainCol))) {
        return { el: mainCol, mode: 'append' };
      }

      // 3. Fallback: el formulario del carrito.
      const cartForm = document.querySelector(CONFIG.selectors.cartForm) ||
                       document.querySelector('form#cart') ||
                       document.querySelector('.cart-section');
      if (cartForm) return { el: cartForm, mode: 'append' };

      return null;
    },

    async insertSelectorsDirectly(cartItems) {
      if (this.isUpdating) {
        console.log('⏸️ Actualización en progreso, omitiendo...');
        return;
      }

      this.isUpdating = true;

      try {
        const newHash = Utils.generateCartHash(cartItems);
        if (CartManager.currentCartHash === newHash &&
            document.getElementById('grinding-selectors-main-container')) {
          console.log('✅ Carrito sin cambios, omitiendo actualización');
          return;
        }
        CartManager.currentCartHash = newHash;
        console.log('🔄 Actualizando selectores...');

        Styles.inject();

        const oldContainer = document.getElementById('grinding-selectors-main-container');
        if (oldContainer) oldContainer.remove();

        const productsNeedingSelection = cartItems.filter(item =>
          CartManager.needsGrindingSelection(item.handle)
        );

        if (productsNeedingSelection.length === 0) {
          console.log('✅ No hay productos de café en el carrito');
          return;
        }

        const target = this.getInsertionTarget();
        if (!target) {
          console.error('❌ No se encontró dónde insertar el selector de molienda');
          return;
        }

        // --- Construcción del bloque ---
        const mainContainer = document.createElement('div');
        mainContainer.id = 'grinding-selectors-main-container';

        const heading = document.createElement('h3');
        heading.className = 'gs-heading';
        heading.textContent = Utils.getText('mainTitle');
        mainContainer.appendChild(heading);

        const description = document.createElement('p');
        description.className = 'gs-description';
        description.textContent = Utils.getText('mainDescription');
        mainContainer.appendChild(description);

        const grid = document.createElement('div');
        grid.className = 'gs-grid';
        mainContainer.appendChild(grid);

        let selectorIndex = 0;

        productsNeedingSelection.forEach(cartItem => {
          for (let i = 0; i < cartItem.quantity; i++) {
            const wrapper = document.createElement('div');
            wrapper.className = 'gs-item';

            const productHeading = document.createElement('div');
            productHeading.className = 'gs-product-title';
            productHeading.textContent = cartItem.product_title +
              (cartItem.quantity > 1 ? ` (${Utils.getText('unit')} ${i + 1})` : '');

            const selectorId = Utils.generateSelectorId(cartItem.handle, selectorIndex);

            const selector = this.createGrindingSelector({
              handle: cartItem.handle,
              title: cartItem.product_title,
              index: selectorIndex,
              selectorId: selectorId,
              variantId: cartItem.variant_id,
              lineItemKey: cartItem.key
            });

            wrapper.appendChild(productHeading);
            wrapper.appendChild(selector);
            grid.appendChild(wrapper);

            selectorIndex++;
          }
        });

        const infoNote = document.createElement('div');
        infoNote.className = 'gs-note';
        infoNote.innerHTML = Utils.getText('infoNote');
        mainContainer.appendChild(infoNote);

        // --- Inserción ---
        if (target.mode === 'after') {
          target.el.insertAdjacentElement('afterend', mainContainer);
        } else {
          target.el.appendChild(mainContainer);
        }

        this.restoreSelections();

        console.log('✅ Selectores insertados en:', target.el, `(${target.mode})`);

      } finally {
        this.isUpdating = false;
      }
    }
  };

  // ========================================
  // OBSERVADOR DE CAMBIOS EN EL CARRITO
  // ========================================

  const CartObserver = {
    updateTimeout: null,
    lastCartState: null,
    intervalId: null,

    init() {
      this.setupCartEventListeners();
      this.setupQuantityButtonListeners();

      if (!this.intervalId) {
        this.intervalId = setInterval(() => this.checkCartChanges(), 2000);
      }
    },

    async checkCartChanges() {
      const cartItems = await CartManager.getCartItems();
      const newHash = Utils.generateCartHash(cartItems);

      const containerMissing = !document.getElementById('grinding-selectors-main-container');

      if (this.lastCartState !== newHash || containerMissing) {
        this.lastCartState = newHash;
        await UIManager.insertSelectorsDirectly(cartItems);
      }
    },

    setupCartEventListeners() {
      document.addEventListener('cart:updated', () => this.scheduleUpdate());
    },

    setupQuantityButtonListeners() {
      document.addEventListener('click', (e) => {
        const button = e.target.closest('button[name="plus"], button[name="minus"], button.quantity__button');
        if (button) this.scheduleUpdate();
      });

      document.addEventListener('change', (e) => {
        if (e.target.matches('input[type="number"][name="updates[]"]') ||
            e.target.matches('input.quantity__input')) {
          this.scheduleUpdate();
        }
      });
    },

    scheduleUpdate() {
      clearTimeout(this.updateTimeout);
      this.updateTimeout = setTimeout(async () => {
        CartManager.currentCartHash = null; // forzar reconstrucción tras cambio de cantidad
        const cartItems = await CartManager.getCartItems();
        await UIManager.insertSelectorsDirectly(cartItems);
      }, 1000);
    }
  };

  // ========================================
  // INICIALIZACIÓN
  // ========================================

  const GrindingSelector = {
    async init() {
      console.log('🔧 Iniciando selector de molienda v7.0...');

      const cartItems = await CartManager.getCartItems();

      if (cartItems.length === 0) {
        console.log('🛒 Carrito vacío');
        return;
      }

      const productsNeedingSelection = cartItems.filter(item =>
        CartManager.needsGrindingSelection(item.handle)
      );

      if (productsNeedingSelection.length === 0) {
        console.log('✅ No hay productos de café en el carrito');
        return;
      }

      await UIManager.insertSelectorsDirectly(cartItems);

      this.setupCheckoutListener();

      CartObserver.init();

      console.log('✅ Selector de molienda inicializado correctamente');
    },

    setupCheckoutListener() {
      const checkoutButton = document.querySelector(CONFIG.selectors.checkoutButton);
      if (checkoutButton) {
        checkoutButton.addEventListener('click', async () => {
          console.log('💾 Guardando selecciones finales...');
          await UIManager.updateCartWithSelections();
          setTimeout(() => Utils.clearLocalStorage(), 2000);
        });
      }
    }
  };

  // ========================================
  // EJECUTAR AL CARGAR
  // ========================================

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      setTimeout(() => GrindingSelector.init(), 500);
    });
  } else {
    setTimeout(() => GrindingSelector.init(), 500);
  }

})();
